import { isPending } from '@fleetcore/shared'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { MetricTile } from '../../components/metric-tile'
import { useFormatters, type Formatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import type { DashboardTile, PeriodComparison } from './api-types'
import {
  coverageBreakdown,
  deltaTone,
  formatSignedPercentPoints,
  formatTileValue,
  withLocalisedReason,
} from './format'
import {
  maintenanceBreakdownOf,
  presentationFor,
  segmentCountsOf,
  severityBreakdownOf,
  UNKNOWN_TILE_ICON,
} from './tiles'

/**
 * One KPI tile: a number, why it is missing when it is, and a way through to the
 * evidence.
 *
 * ─── RULE 9: THE WHOLE TILE IS THE LINK ──────────────────────────────────────
 *
 * Not an arrow in the corner. "42 alertes actives" is a prompt to go and look, and a
 * manager who cannot get from the figure to the list stops believing the figure. The
 * route is the API's — `dashboards.tiles.ts` mirrors the web app's paths deliberately,
 * so a tile the server adds arrives already knowing where it goes.
 *
 * ─── AND `MetricTile` IS REUSED RATHER THAN REIMPLEMENTED ────────────────────
 *
 * It already refuses to render a `PendingMetric` as a number, which is the single
 * property this screen cannot get wrong. What is added around it is the chrome — the
 * card is the anchor, so hover and focus belong to the link — and a footer carrying the
 * comparison and the breakdown. The card styling is moved OUT of `MetricTile` with
 * `border-0 bg-transparent shadow-none` (`cn` is tailwind-merge, so the later class
 * wins) rather than nesting one bordered box inside another.
 */

type ChipTone = 'danger' | 'warning' | 'muted'

const CHIP_CLASS: Readonly<Record<ChipTone, string>> = {
  danger: 'text-xs font-medium text-danger',
  warning: 'text-xs font-medium text-warning',
  muted: 'text-xs text-foreground-subtle',
}

const DELTA_CLASS = {
  success: 'text-success',
  danger: 'text-danger',
  neutral: 'text-foreground-subtle',
} as const

export interface KpiTileProps {
  readonly tile: DashboardTile
  /**
   * The window this tile's comparison covers, when it has one. Supplied by the page
   * because the tiles do not agree: "km aujourd'hui" compares against yesterday to the
   * same hour, "coût du mois" against the previous month to the same day.
   */
  readonly period?: PeriodComparison | null
  readonly isLoading?: boolean
  readonly className?: string
}

export function KpiTile({ tile, period = null, isLoading = false, className }: KpiTileProps) {
  const { t } = useTranslation()
  const formatters = useFormatters()

  const presentation = presentationFor(tile.key)
  const label = presentation
    ? t(`dashboard:tiles.${presentation.i18nKey}.label`)
    : t('dashboard:tiles.unknown.label', { key: tile.key })
  const hint = presentation
    ? t(`dashboard:tiles.${presentation.i18nKey}.hint`)
    : t('dashboard:tiles.unknown.hint')

  const chips = breakdownChips(tile, t, formatters)
  const delta = deltaText(tile, period, t, formatters)

  return (
    <Link
      to={tile.route}
      className={cn(
        'group flex min-w-0 flex-col rounded-lg border border-border bg-surface shadow-card',
        'transition-colors hover:border-border-strong hover:bg-surface-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        className,
      )}
    >
      <MetricTile
        className="border-0 bg-transparent shadow-none"
        compactOnPhone
        label={label}
        hint={hint}
        icon={presentation?.icon ?? UNKNOWN_TILE_ICON}
        isLoading={isLoading}
        metric={withLocalisedReason(tile.metric)}
        format={(value) => formatTileValue(tile, formatters.locale, value)}
      />

      {isLoading || (delta === null && chips.length === 0) ? null : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border px-4 py-2">
          {delta === null ? null : (
            <span
              title={delta.basis ?? undefined}
              className={cn(
                'inline-flex items-center gap-1 text-xs font-medium',
                DELTA_CLASS[delta.tone],
              )}
            >
              <DeltaIcon direction={delta.sign} />
              {delta.text}
              {/* On a phone the tile is half the screen wide, and "vs les 12 premiers
                  jours de la période précédente" became a column five lines tall. The
                  figure stays; the basis is a tap away on the analytics page. */}
              {delta.basis === null ? null : (
                <span className="hidden font-normal text-foreground-subtle sm:inline">
                  {delta.basis}
                </span>
              )}
            </span>
          )}

          {chips.map((chip) => (
            <span key={chip.key} className={CHIP_CLASS[chip.tone]}>
              {chip.text}
            </span>
          ))}
        </div>
      )}
    </Link>
  )
}

function DeltaIcon({ direction }: { direction: 1 | 0 | -1 }): ReactElement {
  if (direction > 0) return <ArrowUpRight aria-hidden className="h-3.5 w-3.5" />
  if (direction < 0) return <ArrowDownRight aria-hidden className="h-3.5 w-3.5" />
  return <Minus aria-hidden className="h-3.5 w-3.5" />
}

interface DeltaText {
  readonly text: string
  readonly tone: keyof typeof DELTA_CLASS
  readonly sign: 1 | 0 | -1
  readonly basis: string | null
}

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * The comparison, and the sentence that stops it lying.
 *
 * `deltaPct` is ALWAYS computed against `previousToDate` — the previous period cut at
 * the same elapsed offset — so the figure itself is already honest. What it needs is a
 * label saying so: on the 12th of the month, "vs le mois précédent" reads as a
 * comparison against thirty-one days, and a fleet doing exactly what it did last month
 * looks like one that collapsed. `partial_vs_complete` therefore prints both counts.
 */
function deltaText(
  tile: DashboardTile,
  period: PeriodComparison | null,
  t: Translate,
  formatters: Formatters,
): DeltaText | null {
  const comparison = tile.comparison
  if (comparison === null) return null

  const delta = comparison.deltaPct
  if (isPending(delta)) {
    // A change from a zero base is undefined — not 100 %, not 0 %. Naming which of the
    // two reasons applies costs one short phrase and stops "—" being read as no change.
    const key =
      delta.reasonKey === 'analytics.previous_period_zero'
        ? 'dashboard:delta.noBase'
        : 'dashboard:delta.unavailable'
    return { text: t(key), tone: 'neutral', sign: 0, basis: null }
  }

  return {
    text: formatSignedPercentPoints(formatters.locale, delta.value),
    tone: deltaTone(tile.direction, delta.value),
    sign: delta.value > 0 ? 1 : delta.value < 0 ? -1 : 0,
    basis: basisLabel(period, t),
  }
}

export function basisLabel(period: PeriodComparison | null, t: Translate): string | null {
  if (period === null) return null
  if (period.basis === 'like_for_like') return t('dashboard:delta.vsPrevious')

  const { unit, elapsed, total } = coverageBreakdown(period.coverage)
  return t(unit === 'day' ? 'dashboard:delta.vsPreviousDays' : 'dashboard:delta.vsPreviousHours', {
    elapsed,
    total,
  })
}

interface Chip {
  readonly key: string
  readonly tone: ChipTone
  readonly text: string
}

/**
 * The breakdown behind a headline.
 *
 * Zero-valued entries are NOT rendered. "0 critique" is true, and it is also noise on a
 * tile meant to be read in half a second — and a row of zeros beside a pending figure
 * is precisely the visual this dashboard exists to avoid.
 */
function breakdownChips(tile: DashboardTile, t: Translate, formatters: Formatters): Chip[] {
  const chips: Chip[] = []

  const severity = severityBreakdownOf(tile)
  if (severity !== null) {
    if (severity.critical > 0) {
      chips.push({
        key: 'critical',
        tone: 'danger',
        text: t('dashboard:breakdown.critical', { n: formatters.integer(severity.critical) }),
      })
    }
    if (severity.warning > 0) {
      chips.push({
        key: 'warning',
        tone: 'warning',
        text: t('dashboard:breakdown.warning', { n: formatters.integer(severity.warning) }),
      })
    }
    return chips
  }

  const maintenance = maintenanceBreakdownOf(tile)
  if (maintenance !== null) {
    if (maintenance.overdue > 0) {
      chips.push({
        key: 'overdue',
        tone: 'danger',
        text: t('dashboard:breakdown.overdue', { n: formatters.integer(maintenance.overdue) }),
      })
    }
    if (maintenance.grounded > 0) {
      chips.push({
        key: 'grounded',
        tone: 'danger',
        text: t('dashboard:breakdown.grounded', { n: formatters.integer(maintenance.grounded) }),
      })
    }
    if (maintenance.cannotEvaluate > 0) {
      // Deliberately NOT folded into the headline, here or on the server: a plan no
      // reading can answer is an installer's queue, and inflating a workshop manager's
      // number with rows no mechanic can act on pushes them to delete the plans.
      chips.push({
        key: 'cannotEvaluate',
        tone: 'muted',
        text: t('dashboard:breakdown.cannotEvaluate', {
          n: formatters.integer(maintenance.cannotEvaluate),
        }),
      })
    }
    return chips
  }

  const segment = segmentCountsOf(tile)
  if (segment !== null && segment.vehicles > 0) {
    chips.push({
      key: 'reporting',
      tone: 'muted',
      text: t('dashboard:breakdown.reporting', {
        reporting: formatters.integer(segment.reportingVehicles),
        vehicles: formatters.integer(segment.vehicles),
      }),
    })
  }
  return chips
}
